/**
 * ChildFormRegistry Service - Zustand Store
 * Single Responsibility: Track the child forms of a FormContainer, their schemas, the values
 * kept when a step is left, and the validation requests that make a child show its errors.
 *
 * Every entry is keyed by childFormKey(scope, formID). The scope is the id of the container
 * control, so two containers on one page that share a child form never see each other's state.
 */

import { create } from 'zustand';
// The engine that renders each child form (validate() is synchronous).
import { FormEngine } from './form.engine';
import type { FormSchema } from '@atlas-forms/types-js';

type ValuesRecord = Record<string, unknown>;
type ValidationOutcome = ReturnType<FormEngine['validate']>;

/**
 * Registry key of one child form inside one container. Both parts are URI-encoded, so the
 * separator "::" can never appear inside the scope and a scope prefix matches only its own entries.
 */
export function childFormKey(scope: string, formID: string): string {
  return `${encodeURIComponent(scope)}::${encodeURIComponent(formID)}`;
}

export interface ChildFormRegistryState {
  /** Engines of the child forms that are mounted right now */
  forms: Map<string, FormEngine>;
  /** Schema of every child form in the container (mounted or not) */
  schemas: Map<string, FormSchema>;
  /** Values a child had when its step was left, restored when the step is shown again */
  snapshots: Map<string, ValuesRecord>;
  /** Current values a child should start from when it has no kept snapshot (e.g. master data) */
  fallbacks: Map<string, () => ValuesRecord>;
  /** Bumped to ask a child to show its validation errors */
  validationTicks: Record<string, number>;
}

export interface ChildFormRegistryActions {
  /** Register a mounted child form */
  register: (key: string, engine: FormEngine) => void;

  /** Unregister a child form */
  unregister: (key: string) => void;

  /** Get a mounted child form by key */
  get: (key: string) => FormEngine | undefined;

  /** Get all mounted child forms */
  getAll: () => FormEngine[];

  /** Unregister all mounted child forms */
  unregisterAll: () => void;

  /** Check whether a child form is mounted */
  has: (key: string) => boolean;

  /** Record the schema of a child form */
  setSchema: (key: string, schema: FormSchema) => void;

  /** Provide the values a child starts from when it has no kept snapshot; read at validation time */
  setFallback: (key: string, values: () => ValuesRecord) => void;

  /** Keep the values of a child form when its step is left */
  snapshot: (key: string, values: ValuesRecord) => void;

  /** Values kept for a child form, if any */
  getSnapshot: (key: string) => ValuesRecord | undefined;

  /** Forget everything registered under a container scope (used when the container unmounts) */
  clearScope: (scope: string) => void;

  /**
   * Drop the mounted engines, kept values and validation requests of a container. Its schemas and
   * fallbacks stay, because FormRenderer owns them and they must survive the container unmounting.
   */
  releaseLive: (scope: string) => void;

  /**
   * Validate a child form: the mounted engine if there is one, otherwise a fresh engine built
   * from its schema and its kept or fallback values. Returns null when the schema is not known yet.
   */
  validateChild: (key: string) => ValidationOutcome | null;

  /** Ask a child form to show its validation errors */
  bumpValidation: (key: string) => void;
}

export type ChildFormRegistryStore = ChildFormRegistryState & ChildFormRegistryActions;

/** Returns a copy of `map` without the entries whose key starts with `prefix` */
function withoutPrefix<V>(map: Map<string, V>, prefix: string): Map<string, V> {
  const next = new Map<string, V>();
  map.forEach((value, key) => {
    if (!key.startsWith(prefix)) next.set(key, value);
  });
  return next;
}

export const useChildFormRegistryStore = create<ChildFormRegistryStore>(
  (set, get) => ({
    forms: new Map<string, FormEngine>(),
    schemas: new Map<string, FormSchema>(),
    snapshots: new Map<string, ValuesRecord>(),
    fallbacks: new Map<string, () => ValuesRecord>(),
    validationTicks: {},

    register: (key: string, engine: FormEngine): void => {
      set((state) => {
        const newForms = new Map(state.forms);
        newForms.set(key, engine);
        return { forms: newForms };
      });
    },

    unregister: (key: string): void => {
      set((state) => {
        const newForms = new Map(state.forms);
        newForms.delete(key);
        return { forms: newForms };
      });
    },

    get: (key: string): FormEngine | undefined => {
      return get().forms.get(key);
    },

    getAll: (): FormEngine[] => {
      return Array.from(get().forms.values());
    },

    unregisterAll: (): void => {
      set({ forms: new Map() });
    },

    has: (key: string): boolean => {
      return get().forms.has(key);
    },

    setSchema: (key: string, schema: FormSchema): void => {
      set((state) => {
        const newSchemas = new Map(state.schemas);
        newSchemas.set(key, schema);
        return { schemas: newSchemas };
      });
    },

    setFallback: (key: string, values: () => ValuesRecord): void => {
      set((state) => {
        const newFallbacks = new Map(state.fallbacks);
        newFallbacks.set(key, values);
        return { fallbacks: newFallbacks };
      });
    },

    snapshot: (key: string, values: ValuesRecord): void => {
      set((state) => {
        const newSnapshots = new Map(state.snapshots);
        newSnapshots.set(key, { ...values });
        return { snapshots: newSnapshots };
      });
    },

    getSnapshot: (key: string): ValuesRecord | undefined => {
      return get().snapshots.get(key);
    },

    clearScope: (scope: string): void => {
      const prefix = childFormKey(scope, '');
      set((state) => ({
        forms: withoutPrefix(state.forms, prefix),
        schemas: withoutPrefix(state.schemas, prefix),
        snapshots: withoutPrefix(state.snapshots, prefix),
        fallbacks: withoutPrefix(state.fallbacks, prefix),
        validationTicks: Object.fromEntries(
          Object.entries(state.validationTicks).filter(([key]) => !key.startsWith(prefix))
        ),
      }));
    },

    releaseLive: (scope: string): void => {
      const prefix = childFormKey(scope, '');
      set((state) => ({
        forms: withoutPrefix(state.forms, prefix),
        snapshots: withoutPrefix(state.snapshots, prefix),
        validationTicks: Object.fromEntries(
          Object.entries(state.validationTicks).filter(([key]) => !key.startsWith(prefix))
        ),
      }));
    },

    validateChild: (key: string): ValidationOutcome | null => {
      const live = get().forms.get(key);
      if (live) return live.validate();
      const schema = get().schemas.get(key);
      if (!schema) return null;
      const values = get().snapshots.get(key) ?? get().fallbacks.get(key)?.() ?? {};
      return new FormEngine(schema, values).validate();
    },

    bumpValidation: (key: string): void => {
      set((state) => ({
        validationTicks: { ...state.validationTicks, [key]: (state.validationTicks[key] ?? 0) + 1 },
      }));
    },
  })
);
